import type {Highlight, HighlightDetail, HighlightGeometry} from '../types';

/**
 * Минимальная форма замечания, нужная просмотрщику.
 *
 * Намеренно НЕ импортируем PDFNote конкретного приложения: модели в pda-front
 * и pda-cfront различаются (у контрагента нет PDFNoteTags, Task и т.п.).
 * Структурная типизация TS позволяет обеим моделям подойти сюда как есть.
 */
export interface MappableNote {
    Id?: number;
    Title?: string;
    PageInfo?: string;
    StatusId?: number;
    Status?: { Title?: string };
    IsCritical?: boolean;
    CodeId?: number;
    CommentsCount?: number;
    Created?: Date | string;
    CreatedById?: number;
    CreatedBy?: { Title?: string } | unknown;
    NoteData?: HighlightGeometry[] | HighlightGeometry | null;
    Attachments?: Array<{ Id?: number; AttachmentId: string; Attachment?: { Title?: string } }>;
    PDFNoteTags?: Array<{ TagTypeId?: number; TagType?: { Title?: string } }>;
    PDFNoteWorkflowInstances?: Array<{ CompletionComment?: string; CompletionDate?: Date | string }>;
}

/**
 * Резолверы css-классов бейджей передаются параметрами, а не импортируются:
 * словарь статусов у контрагента намеренно упрощён (Выполнено / Не выполнено /
 * Гарантийное), и общий код не должен навязывать ему семантику pda-front.
 */
export interface NoteMappingOptions {
    currentUserId: number;
    statusBadge?: (statusId?: number) => string;
    tagBadge?: (tagTypeId?: number) => string;
    /** Подпись к комментарию исполнителя. В pda-front — «Ответ КА». */
    contractorCommentLabel?: string;
    /**
     * Принудительный номер страницы для всех хайлайтов.
     * Word рендерится одним потоком без страниц — там всегда 1.
     * Для PDF не задаётся: берётся geo.page, иначе note.PageInfo.
     */
    forcePageNumber?: number;
}

export function buildDetail(note: MappableNote, options: NoteMappingOptions): HighlightDetail {
    const {currentUserId, statusBadge, tagBadge, contractorCommentLabel = 'Ответ КА'} = options;

    const contractorInstance = [...(note.PDFNoteWorkflowInstances ?? [])]
        .reverse()
        .find(i => i.CompletionComment);
    const uniqueTags = [...new Map((note.PDFNoteTags ?? []).map(t => [t.TagType?.Title, t])).values()];

    return {
        noteId: note.Id,
        isOwnNote: note.CreatedById === currentUserId,
        titleHtml: note.Title ?? '',
        statusTitle: note.Status?.Title,
        statusBadgeClass: statusBadge?.(note.StatusId) ?? '',
        isCritical: note.IsCritical,
        attachments: (note.Attachments ?? []).map(a => ({
            id: a.Id ?? 0,
            attachmentId: a.AttachmentId,
            title: a.Attachment?.Title ?? '',
        })),
        tags: uniqueTags.map(t => ({
            title: t.TagType?.Title ?? '',
            badgeClass: tagBadge?.(t.TagTypeId) ?? '',
        })),
        commentsCount: note.CommentsCount ?? 0,
        contractorComment: contractorInstance?.CompletionComment,
        contractorCommentDate: contractorInstance?.CompletionDate?.toString(),
        contractorCommentLabel,
    };
}

/**
 * Одно замечание -> массив хайлайтов (по одному на геометрию в NoteData).
 * Формат id: `${noteId}-${index}` — по нему просмотрщик группирует геометрии
 * одного замечания в сайдбаре и находит замечание для initialScrollToNoteId.
 */
export function noteToHighlights(note: MappableNote, options: NoteMappingOptions): Highlight[] {
    const geoArr: HighlightGeometry[] = Array.isArray(note.NoteData)
        ? note.NoteData
        : note.NoteData
            ? [note.NoteData as HighlightGeometry]
            : [];

    const detail = buildDetail(note, options);
    const fallbackPage = options.forcePageNumber ?? (parseInt(note.PageInfo ?? '', 10) || 1);
    const createdByTitle: string | undefined = (note.CreatedBy as any)?.Title;
    const base = {
        note: note.Title ?? '',
        isCritical: note.IsCritical,
        codeId: note.CodeId,
        createdAt: note.Created ? note.Created.toString() : new Date().toISOString(),
        createdBy: createdByTitle,
        detail,
    };

    if (geoArr.length === 0) {
        // Заметка привязана к документу, но не содержит геометрии на странице -
        // всё равно показываем её в сайдбаре (просто нечего подсвечивать на канвасе)
        return [{
            id: `${note.Id}-0`,
            type: 'none',
            pageNumber: fallbackPage,
            ...base,
        } as Highlight];
    }

    return geoArr.map((geo, index) => ({
        ...geo,
        id: `${note.Id}-${index}`,
        pageNumber: options.forcePageNumber ?? geo.page ?? fallbackPage,
        ...base,
    } as Highlight));
}
